import axiosInstance from './axiosInstance';
import { useMutation, useQueries, useQuery, type UseMutationOptions, type UseQueryOptions } from '@tanstack/react-query';
import { AdvanceFilter } from '@/types/types';
import type {
  CreateDdiSearchResultPayload,
  CreateDdiSearchResultResponse,
  DdiCommentGetAllResponse,
  DdiCommentCreateResponse,
  DdiInvoiceEmailShareRequest,
  DdiInvoiceEmailShareResponse,
  DdiInvoicePatchRequest,
  DdiInvoicePatchResponse,
  DdiInvoiceResponse,
  DdiLookupItem,
  DdiWorkOrderAssignResponse,
  DdiWorkOrderCreateRequest,
  DdiWorkOrderCreateResponse,
  DdiWorkOrderDetailResponse,
  DdiWorkOrderEditResponse,
  DdiWorkOrderGetAllRequest,
  DdiWorkOrderGetAllResponse,
  DdiWorkOrderStatus,
  DdiWorkOrderUpdateRequest,
  DdiWorkOrderUpdateStatusResponse,
  GetDdiSearchResultResponse,
  UpdateDdiSearchResultPayload,
  UpdateDdiSearchResultResponse,
} from '@/types/dueDiligence.types';
import { normalizeAxiosError } from '@/lib/error';
import { getFileNameFromContentDisposition } from '@/lib/utils';
import type { GenericErrorResponse, InternalServerErrorResponse } from '@/types/api.types';

type DdiApiResult<T> = { data?: T; error?: GenericErrorResponse | InternalServerErrorResponse };

/** Trim values and drop incomplete filter rows before getAll. */
export const ddiFiltersToParams = (filters: AdvanceFilter[]): AdvanceFilter[] =>
  filters
    .map((f) => ({
      ...f,
      value: typeof f.value === 'string' ? f.value.trim() : f.value,
    }))
    .filter((f) => !!f.field && !!f.operator && !!f.fieldType && !!f.value);

export const getDdiWorkOrders = async (
  params: DdiWorkOrderGetAllRequest
): Promise<{ data?: DdiWorkOrderGetAllResponse; error?: GenericErrorResponse | InternalServerErrorResponse }> => {
  try {
    const response = await axiosInstance.post<DdiWorkOrderGetAllResponse>(
      '/due-diligence/work-orders/getAll',
      params
    );
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useDDIWorkOrders = (
  params: DdiWorkOrderGetAllRequest,
  options?: { query?: UseQueryOptions<DdiWorkOrderGetAllResponse> }
) => {
  const queryOptions = options?.query;
  const queryKey = queryOptions?.queryKey
    ? ['ddi-work-orders', params, queryOptions.queryKey]
    : ['ddi-work-orders', params];

  return useQuery<DdiWorkOrderGetAllResponse>({
    ...queryOptions,
    queryKey,
    queryFn: async () => {
      const { data, error } = await getDdiWorkOrders(params);
      if (error) throw error;
      return data!;
    },
  });
};

export const getDdiWorkOrderDetail = async (
  id: string
): Promise<{ data?: DdiWorkOrderDetailResponse; error?: GenericErrorResponse | InternalServerErrorResponse }> => {
  try {
    const response = await axiosInstance.get<DdiWorkOrderDetailResponse>(`/due-diligence/work-orders/${id}`);
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useDDIWorkOrderDetail = (id: string | null) => {
  return useQuery<DdiWorkOrderDetailResponse>({
    queryKey: ['ddi-work-order-detail', id],
    queryFn: async () => {
      const { data, error } = await getDdiWorkOrderDetail(id!);
      if (error) throw error;
      return data!;
    },
    enabled: id !== null,
  });
};

export const getDdiSearchResult = async (
  workOrderId: string
): Promise<{
  data?: GetDdiSearchResultResponse | null;
  error?: GenericErrorResponse | InternalServerErrorResponse;
}> => {
  try {
    const response = await axiosInstance.get<GetDdiSearchResultResponse | null>(
      `/due-diligence/work-orders/${workOrderId}/search-results`
    );
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useDdiSearchResult = (workOrderId: string | null) => {
  return useQuery<GetDdiSearchResultResponse | null>({
    queryKey: ['ddi-search-result', workOrderId],
    queryFn: async () => {
      const { data, error } = await getDdiSearchResult(workOrderId!);
      if (error) throw error;
      return data ?? null;
    },
    enabled: workOrderId !== null,
  });
};

export const createDdiSearchResult = async (
  workOrderId: string,
  payload: CreateDdiSearchResultPayload
): Promise<DdiApiResult<CreateDdiSearchResultResponse>> => {
  try {
    const response = await axiosInstance.post<CreateDdiSearchResultResponse>(
      `/due-diligence/work-orders/${workOrderId}/search-results`,
      payload
    );
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useCreateDdiSearchResult = (options?: {
  mutation?: UseMutationOptions<
    CreateDdiSearchResultResponse,
    GenericErrorResponse | InternalServerErrorResponse,
    { workOrderId: string; payload: CreateDdiSearchResultPayload }
  >;
}) =>
  useMutation({
    mutationFn: async ({
      workOrderId,
      payload,
    }: {
      workOrderId: string;
      payload: CreateDdiSearchResultPayload;
    }) => {
      const { data, error } = await createDdiSearchResult(workOrderId, payload);
      if (error) throw error;
      return data!;
    },
    ...options?.mutation,
  });

export const updateDdiSearchResult = async (
  workOrderId: string,
  payload: UpdateDdiSearchResultPayload
): Promise<DdiApiResult<UpdateDdiSearchResultResponse>> => {
  try {
    const response = await axiosInstance.patch<UpdateDdiSearchResultResponse>(
      `/due-diligence/work-orders/${workOrderId}/search-results`,
      payload
    );
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useUpdateDdiSearchResult = (options?: {
  mutation?: UseMutationOptions<
    UpdateDdiSearchResultResponse,
    GenericErrorResponse | InternalServerErrorResponse,
    { workOrderId: string; payload: UpdateDdiSearchResultPayload }
  >;
}) =>
  useMutation({
    mutationFn: async ({
      workOrderId,
      payload,
    }: {
      workOrderId: string;
      payload: UpdateDdiSearchResultPayload;
    }) => {
      const { data, error } = await updateDdiSearchResult(workOrderId, payload);
      if (error) throw error;
      return data!;
    },
    ...options?.mutation,
  });

export const getDdiInvoice = async (
  id: string
): Promise<{ data?: DdiInvoiceResponse | null; error?: GenericErrorResponse | InternalServerErrorResponse }> => {
  try {
    const response = await axiosInstance.get<DdiInvoiceResponse | null>(`/due-diligence/work-orders/${id}/invoice`);
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useDdiInvoice = (id: string | null) => {
  return useQuery<DdiInvoiceResponse | null>({
    queryKey: ['ddi-invoice', id],
    queryFn: async () => {
      const { data, error } = await getDdiInvoice(id!);
      if (error) throw error;
      return data ?? null;
    },
    enabled: id !== null,
  });
};

export const getDdiInvoiceByNumber = async (
  invoiceNumber: string
): Promise<{ data?: DdiInvoiceResponse | null; error?: GenericErrorResponse | InternalServerErrorResponse }> => {
  try {
    const response = await axiosInstance.get<DdiInvoiceResponse>(
      `/due-diligence/invoice/${encodeURIComponent(invoiceNumber)}`
    );
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useDdiInvoiceByNumber = (invoiceNumber: string | undefined, refreshKey?: number) => {
  return useQuery<DdiInvoiceResponse | null>({
    queryKey: ['ddi-invoice-by-number', invoiceNumber, refreshKey],
    queryFn: async () => {
      const { data, error } = await getDdiInvoiceByNumber(invoiceNumber!);
      if (error) throw error;
      return data ?? null;
    },
    enabled: !!invoiceNumber,
  });
};

export const patchDdiInvoice = async (
  workOrderId: string,
  body: DdiInvoicePatchRequest
): Promise<{ data?: DdiInvoicePatchResponse; error?: GenericErrorResponse | InternalServerErrorResponse }> => {
  try {
    const response = await axiosInstance.patch<DdiInvoicePatchResponse>(
      `/due-diligence/work-orders/${workOrderId}/invoice`,
      body
    );
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const usePatchDdiInvoiceMutation = (
  options?: UseMutationOptions<DdiInvoicePatchResponse, unknown, { workOrderId: string; body: DdiInvoicePatchRequest }>
) => {
  return useMutation({
    mutationFn: async ({ workOrderId, body }) => {
      const { data, error } = await patchDdiInvoice(workOrderId, body);
      if (error) throw error;
      return data!;
    },
    ...options,
  });
};

export const downloadDdiInvoicePdf = async (
  invoiceId: string
): Promise<{ blob: Blob; fileName?: string } | undefined> => {
  try {
    const response = await axiosInstance.get<Blob>(`/due-diligence/invoice/${invoiceId}/pdf`, {
      responseType: 'blob',
      // Puppeteer cold-start can exceed the default 15s axios timeout.
      timeout: 60000,
    });

    const contentType = String(response.headers['content-type'] ?? '');
    if (contentType.includes('application/json')) {
      const text = await response.data.text();
      throw new Error(text || 'PDF export returned an error payload');
    }

    const fileName = getFileNameFromContentDisposition(
      response.headers['content-disposition'] as string | undefined
    );
    return { blob: response.data, fileName };
  } catch (error) {
    console.error('DDI invoice PDF download failed', error);
    return undefined;
  }
};

export const shareDdiInvoiceEmail = async (
  invoiceId: string
): Promise<{
  data?: DdiInvoiceEmailShareResponse;
  error?: GenericErrorResponse | InternalServerErrorResponse;
}> => {
  try {
    const body: DdiInvoiceEmailShareRequest = { invoiceId };
    const response = await axiosInstance.post<DdiInvoiceEmailShareResponse>(
      `/due-diligence/invoice/${invoiceId}/email`,
      body
    );
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const getDDIWorkOrderComments = async (
  id: string
): Promise<{ data?: DdiCommentGetAllResponse; error?: GenericErrorResponse | InternalServerErrorResponse }> => {
  try {
    const response = await axiosInstance.get<DdiCommentGetAllResponse>(`/due-diligence/work-orders/${id}/comments`);
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useDDIWorkOrderComments = (id: string | null) => {
  return useQuery<DdiCommentGetAllResponse>({
    queryKey: ['ddi-work-order-comments', id],
    queryFn: async () => {
      const { data, error } = await getDDIWorkOrderComments(id!);
      if (error) throw error;
      return data!;
    },
    enabled: id !== null,
  });
};

export const addDDIWorkOrderComment = async (
  id: string,
  commentText: string
): Promise<{ data?: DdiCommentCreateResponse; error?: GenericErrorResponse | InternalServerErrorResponse }> => {
  try {
    const response = await axiosInstance.post<DdiCommentCreateResponse>(`/due-diligence/work-orders/${id}/comments`, {
      commentText,
    });
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

const toDdiLookupOptions = (items: DdiLookupItem[]) => items.map((item) => ({ label: item.name, value: item.name }));

export const getDdiTypes = async (): Promise<DdiApiResult<DdiLookupItem[]>> => {
  try {
    const response = await axiosInstance.get<DdiLookupItem[]>('/due-diligence/types');
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useDDITypes = () =>
  useQuery<DdiLookupItem[]>({
    queryKey: ['ddi-types'],
    queryFn: async () => {
      const { data, error } = await getDdiTypes();
      if (error) throw error;
      return data!;
    },
  });

export const getDdiSubtypes = async (typeId: string): Promise<DdiApiResult<DdiLookupItem[]>> => {
  try {
    const response = await axiosInstance.get<DdiLookupItem[]>(`/due-diligence/types/${typeId}/subtypes`);
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useDDISubtypes = (typeId?: string) =>
  useQuery<DdiLookupItem[]>({
    queryKey: ['ddi-subtypes', typeId],
    queryFn: async () => {
      const { data, error } = await getDdiSubtypes(typeId!);
      if (error) throw error;
      return data!;
    },
    enabled: !!typeId,
  });

export const useDDIAllSubtypeOptions = (options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? true;
  const typesQuery = useDDITypes();
  const types = typesQuery.data ?? [];

  const subtypeQueries = useQueries({
    queries: types.map((type) => ({
      queryKey: ['ddi-subtypes', type.id],
      queryFn: async () => {
        const { data, error } = await getDdiSubtypes(type.id);
        if (error) throw error;
        return data!;
      },
      enabled: enabled && typesQuery.isSuccess,
    })),
  });

  const allSubtypes = subtypeQueries.flatMap((q) => q.data ?? []);
  const deduped = allSubtypes.filter(
    (item, index, all) => all.findIndex((o) => o.name === item.name) === index
  );

  return {
    options: toDdiLookupOptions(deduped),
    isLoading: typesQuery.isLoading || subtypeQueries.some((q) => q.isLoading),
  };
};

export const getDdiReports = async (
  typeId: string,
  subtypeId?: string | null
): Promise<DdiApiResult<DdiLookupItem[]>> => {
  try {
    const response = await axiosInstance.get<DdiLookupItem[]>('/due-diligence/reports', {
      params: subtypeId ? { typeId, subtypeId } : { typeId },
    });
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useDdiReports = (typeId?: string, subtypeId?: string | null) =>
  useQuery<DdiLookupItem[]>({
    queryKey: ['ddi-reports', typeId, subtypeId],
    queryFn: async () => {
      const { data, error } = await getDdiReports(typeId!, subtypeId);
      if (error) throw error;
      return data!;
    },
    enabled: !!typeId,
  });

export const getDdiCaseTypes = async (): Promise<DdiApiResult<DdiLookupItem[]>> => {
  try {
    const response = await axiosInstance.get<DdiLookupItem[]>('/due-diligence/case-types');
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useDDICaseTypes = (options?: { enabled?: boolean }) =>
  useQuery<DdiLookupItem[]>({
    queryKey: ['ddi-case-types'],
    queryFn: async () => {
      const { data, error } = await getDdiCaseTypes();
      if (error) throw error;
      return data!;
    },
    enabled: options?.enabled ?? true,
  });

export const getDdiCaseSubtypes = async (caseType: string): Promise<DdiApiResult<DdiLookupItem[]>> => {
  try {
    const response = await axiosInstance.get<DdiLookupItem[]>(`/due-diligence/case-types/${caseType}/subtypes`);
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useDDICaseSubtypes = (caseType?: string) =>
  useQuery<DdiLookupItem[]>({
    queryKey: ['ddi-case-subtypes', caseType],
    queryFn: async () => {
      const { data, error } = await getDdiCaseSubtypes(caseType!);
      if (error) throw error;
      return data!;
    },
    enabled: !!caseType,
  });

export const useDDIAllCaseSubtypeOptions = (options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? true;
  const caseTypesQuery = useDDICaseTypes({ enabled });
  const caseTypes = caseTypesQuery.data ?? [];

  const caseSubtypeQueries = useQueries({
    queries: caseTypes.map((caseType) => ({
      queryKey: ['ddi-case-subtypes', caseType.name],
      queryFn: async () => {
        const { data, error } = await getDdiCaseSubtypes(caseType.name);
        if (error) throw error;
        return data!;
      },
      enabled: enabled && caseTypesQuery.isSuccess,
    })),
  });

  const allSubtypes = caseSubtypeQueries.flatMap((q) => q.data ?? []);
  const deduped = allSubtypes.filter(
    (item, index, all) => all.findIndex((o) => o.name === item.name) === index
  );

  return {
    options: toDdiLookupOptions(deduped),
    isLoading: caseTypesQuery.isLoading || caseSubtypeQueries.some((q) => q.isLoading),
  };
};

/** Assigns a DDI work order to `userId` (self or another user when caller is admin/supervisor). */
export const assignDdiWorkOrder = async (
  workOrderId: string,
  userId: string
): Promise<DdiApiResult<DdiWorkOrderAssignResponse>> => {
  try {
    const response = await axiosInstance.patch<DdiWorkOrderAssignResponse>(
      `/due-diligence/work-orders/${workOrderId}/assign`,
      { userId }
    );
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

/** @deprecated Prefer `assignDdiWorkOrder` — same endpoint and body. */
export const assignDdiWorkOrderToMe = assignDdiWorkOrder;

export const useAssignDdiWorkOrder = (options?: {
  mutation?: UseMutationOptions<
    DdiWorkOrderAssignResponse,
    GenericErrorResponse | InternalServerErrorResponse,
    { workOrderId: string; userId: string }
  >;
}) =>
  useMutation({
    mutationFn: async ({ workOrderId, userId }: { workOrderId: string; userId: string }) => {
      const { data, error } = await assignDdiWorkOrder(workOrderId, userId);
      if (error) throw error;
      return data!;
    },
    ...options?.mutation,
  });

/** @deprecated Prefer `useAssignDdiWorkOrder`. */
export const useAssignDdiWorkOrderToMe = useAssignDdiWorkOrder;

export const updateDdiWorkOrderStatus = async (
  workOrderId: string,
  status: DdiWorkOrderStatus
): Promise<DdiApiResult<DdiWorkOrderUpdateStatusResponse>> => {
  try {
    const response = await axiosInstance.patch<DdiWorkOrderUpdateStatusResponse>(
      `/due-diligence/work-orders/${workOrderId}/update-status`,
      { status }
    );
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useUpdateDdiWorkOrderStatus = (options?: {
  mutation?: UseMutationOptions<
    DdiWorkOrderUpdateStatusResponse,
    GenericErrorResponse | InternalServerErrorResponse,
    { workOrderId: string; status: DdiWorkOrderStatus }
  >;
}) =>
  useMutation({
    mutationFn: async ({ workOrderId, status }: { workOrderId: string; status: DdiWorkOrderStatus }) => {
      const { data, error } = await updateDdiWorkOrderStatus(workOrderId, status);
      if (error) throw error;
      return data!;
    },
    ...options?.mutation,
  });

export const createDdiWorkOrder = async (
  payload: DdiWorkOrderCreateRequest
): Promise<DdiApiResult<DdiWorkOrderCreateResponse>> => {
  try {
    const response = await axiosInstance.post<DdiWorkOrderCreateResponse>(
      '/due-diligence/work-orders/',
      payload
    );
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useCreateDdiWorkOrder = (options?: {
  mutation?: UseMutationOptions<
    DdiWorkOrderCreateResponse,
    GenericErrorResponse | InternalServerErrorResponse,
    DdiWorkOrderCreateRequest
  >;
}) =>
  useMutation({
    mutationFn: async (payload: DdiWorkOrderCreateRequest) => {
      const { data, error } = await createDdiWorkOrder(payload);
      if (error) throw error;
      return data!;
    },
    ...options?.mutation,
  });

export const fetchDdiWorkOrderForEdit = async (
  id: string
): Promise<DdiApiResult<DdiWorkOrderEditResponse>> => {
  try {
    const response = await axiosInstance.post<DdiWorkOrderEditResponse>(
      `/due-diligence/work-orders/${id}/fetch-for-edit`
    );
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useFetchDdiWorkOrderForEdit = () =>
  useMutation<DdiWorkOrderEditResponse, GenericErrorResponse | InternalServerErrorResponse, string>({
    mutationFn: async (id) => {
      const { data, error } = await fetchDdiWorkOrderForEdit(id);
      if (error) throw error;
      return data!;
    },
  });

export const updateDdiWorkOrder = async (
  id: string,
  payload: DdiWorkOrderUpdateRequest
): Promise<DdiApiResult<DdiWorkOrderEditResponse>> => {
  try {
    const response = await axiosInstance.patch<DdiWorkOrderEditResponse>(
      `/due-diligence/work-orders/${id}/update`,
      payload
    );
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useUpdateDdiWorkOrder = () =>
  useMutation<
    DdiWorkOrderEditResponse,
    GenericErrorResponse | InternalServerErrorResponse,
    { id: string; payload: DdiWorkOrderUpdateRequest }
  >({
    mutationFn: async ({ id, payload }) => {
      const { data, error } = await updateDdiWorkOrder(id, payload);
      if (error) throw error;
      return data!;
    },
  });

export const cancelDdiWorkOrderEdit = async (id: string): Promise<void> => {
  await axiosInstance.post(`/due-diligence/work-orders/${id}/cancel-edit`);
};
